import React, { useEffect, useMemo, useState } from "react";
import {
  ImageGeneratorCatalogSurfaceSlide,
  resolveImageGeneratorResultSrc,
} from "../../app/api/imageGenerator";
import { FORECAST_REPORT_COPY } from "../../constants/imageGeneratorDictionaries";
import CollectionBuildSketchModal from "./CollectionBuildSketchModal";
import {
  SilhouetteSketchAsset,
  silhouetteSketchCaption,
} from "./collectionBuildConstants";
import { FieldLabel, sectionClassName } from "./ImageGeneratorControls";

type Props = {
  silhouetteSlides: ImageGeneratorCatalogSurfaceSlide[];
  detailSlides: ImageGeneratorCatalogSurfaceSlide[];
  selectedSilhouetteId: string;
  selectedDetailId: string;
  loading?: boolean;
  disabled?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectSilhouette: (id: string) => void;
  onSelectDetail: (id: string) => void;
  /** Share a row with the specification control instead of a separate section. */
  inline?: boolean;
};

const NO_SELECTION: string[] = [];

/** Same box size as FieldSelect (32×207, radius 8). Fill and label stay the trend button. */
const trendButtonClassName = (active: boolean) =>
  `box-border flex h-8 w-full min-w-0 max-w-[207px] cursor-pointer items-center justify-center overflow-hidden rounded-[8px] ${
    active ? "border-2" : "border"
  } border-[#737373] bg-[#F6F3F0] px-2 font-avenir text-sm font-semibold text-primary hover:bg-[#F3EDE8] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60`;

const toSketchAssets = (
  slides: ImageGeneratorCatalogSurfaceSlide[]
): SilhouetteSketchAsset[] =>
  slides.map(slide => ({
    stem: slide.id,
    url: slide.image?.available ? slide.image.url : "",
    available: Boolean(slide.image?.available && slide.image?.url),
    label: slide.name,
    reportTitle: slide.reportTitle,
    sliderImageIndex: slide.sliderImageIndex,
  }));

const SelectedSketchThumb: React.FC<{
  asset: SilhouetteSketchAsset;
  caption?: string | null;
  label: string;
  removeLabel: string;
  onRemove?: () => void;
}> = ({ asset, caption, label, removeLabel, onRemove }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    setSrc(null);
    (async () => {
      try {
        const preview = await resolveImageGeneratorResultSrc(asset.url);
        if (cancelled) {
          if (preview.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
          }
          return;
        }
        if (preview.startsWith("blob:")) {
          blobUrl = preview;
        }
        setSrc(preview);
      } catch {
        if (!cancelled) {
          setSrc(asset.url || null);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [asset.url]);

  return (
    <div className="flex w-[72px] shrink-0 flex-col gap-1">
      <p
        className="truncate text-center text-[10px] font-semibold text-tertiary"
        title={label}
      >
        {label}
      </p>
      <div className="relative h-[72px] w-[72px]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-lg border border-[#E8DDD4] bg-white">
          {src ? (
            <img
              src={src}
              alt=""
              className="h-full w-full object-contain p-1"
            />
          ) : (
            <span className="px-1 text-center text-[9px] font-medium uppercase tracking-wide text-tertiary">
              {FORECAST_REPORT_COPY.sketchPlaceholder}
            </span>
          )}
        </div>
        {onRemove ? (
          <button
            type="button"
            aria-label={removeLabel}
            title={removeLabel}
            onMouseDown={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
            className="absolute right-0 top-0 z-10 flex h-4 w-4 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#737373] bg-white text-[11px] leading-none text-secondary shadow-sm hover:bg-[#F5F5F5] focus:outline-none"
          >
            ×
          </button>
        ) : null}
      </div>
      {caption ? (
        <p
          className="truncate text-center text-[11px] leading-[14px] text-secondary"
          title={caption}
        >
          {caption}
        </p>
      ) : null}
    </div>
  );
};

/** Forecast picker: silhouette required (single); detailed optional (single). */
const ImageGeneratorForecastSilhouettePicker: React.FC<Props> = ({
  silhouetteSlides,
  detailSlides,
  selectedSilhouetteId,
  selectedDetailId,
  loading = false,
  disabled = false,
  open,
  onOpenChange,
  onSelectSilhouette,
  onSelectDetail,
  inline = false,
}) => {
  /** Detail modal is local; parent `open` remains silhouette (incl. auto-open). */
  const [detailOpen, setDetailOpen] = useState(false);
  const silhouetteAssets = useMemo(
    () => toSketchAssets(silhouetteSlides),
    [silhouetteSlides]
  );
  const detailAssets = useMemo(
    () => toSketchAssets(detailSlides),
    [detailSlides]
  );
  const silhouetteSelectedIds = useMemo(
    () => (selectedSilhouetteId ? [selectedSilhouetteId] : NO_SELECTION),
    [selectedSilhouetteId]
  );
  const detailSelectedIds = useMemo(
    () => (selectedDetailId ? [selectedDetailId] : NO_SELECTION),
    [selectedDetailId]
  );
  const selectedSilhouette =
    silhouetteAssets.find(asset => asset.stem === selectedSilhouetteId) || null;
  const selectedDetail =
    detailAssets.find(asset => asset.stem === selectedDetailId) || null;
  const silhouetteSession = open;
  const detailSession = detailOpen && !open;
  const modalOpen = silhouetteSession || detailSession;

  useEffect(() => {
    if (open) {
      setDetailOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setDetailOpen(false);
    }
  }, [disabled]);

  const openSilhouetteModal = () => {
    if (disabled) {
      return;
    }
    setDetailOpen(false);
    onOpenChange(true);
  };

  const openDetailModal = () => {
    if (disabled) {
      return;
    }
    if (open) {
      onOpenChange(false);
    }
    setDetailOpen(true);
  };

  const closeModal = () => {
    if (open) {
      onOpenChange(false);
    }
    setDetailOpen(false);
  };

  const silhouetteThumb = selectedSilhouette ? (
    <div className="mt-2">
      <SelectedSketchThumb
        asset={selectedSilhouette}
        label={FORECAST_REPORT_COPY.silhouetteToggle}
        caption={silhouetteSketchCaption(selectedSilhouette, 0, 1)}
        removeLabel={FORECAST_REPORT_COPY.removeSilhouette}
        onRemove={
          disabled
            ? undefined
            : () => {
                onSelectSilhouette("");
              }
        }
      />
    </div>
  ) : null;

  const detailThumb = selectedDetail ? (
    <div className="mt-2">
      <SelectedSketchThumb
        asset={selectedDetail}
        label={FORECAST_REPORT_COPY.detailedToggle}
        caption={silhouetteSketchCaption(selectedDetail, 0, 1)}
        removeLabel={FORECAST_REPORT_COPY.removeDetailed}
        onRemove={
          disabled
            ? undefined
            : () => {
                onSelectDetail("");
              }
        }
      />
    </div>
  ) : null;

  // Inline: sm:col-span-2 so both 207px columns fit in StyleFields' grid tracks.
  const trendControl = (
    <div
      className={
        inline
          ? "flex w-max max-w-full flex-row flex-wrap items-start gap-x-3 gap-y-2 self-start sm:col-span-2"
          : "flex w-max max-w-full flex-row flex-wrap items-start gap-x-3 gap-y-2"
      }
    >
      <div className="flex w-[207px] shrink-0 flex-col">
        <FieldLabel as="span" required>
          {FORECAST_REPORT_COPY.chooseTrend}
        </FieldLabel>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={silhouetteSession}
          aria-pressed={Boolean(selectedSilhouetteId)}
          onClick={openSilhouetteModal}
          className={trendButtonClassName(Boolean(selectedSilhouetteId))}
        >
          <span className="max-w-full truncate">
            {FORECAST_REPORT_COPY.specificSketchOnly}
          </span>
        </button>
        {silhouetteThumb}
      </div>
      <div className="flex w-[207px] shrink-0 flex-col">
        <FieldLabel as="span">
          {FORECAST_REPORT_COPY.chooseDetailTrend}
        </FieldLabel>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={detailSession}
          aria-pressed={Boolean(selectedDetailId)}
          onClick={openDetailModal}
          className={trendButtonClassName(Boolean(selectedDetailId))}
        >
          <span className="max-w-full truncate">
            {FORECAST_REPORT_COPY.specificSketchOnly}
          </span>
        </button>
        {detailThumb}
      </div>
    </div>
  );

  const sketchModal = (
    <CollectionBuildSketchModal
      open={modalOpen}
      assets={silhouetteSession ? silhouetteAssets : detailAssets}
      loading={loading}
      selectedIds={
        silhouetteSession ? silhouetteSelectedIds : detailSelectedIds
      }
      selectionMode="single"
      primaryEmptyMessage={
        silhouetteSession
          ? FORECAST_REPORT_COPY.silhouetteEmpty
          : FORECAST_REPORT_COPY.detailedEmpty
      }
      primaryLoadingMessage={FORECAST_REPORT_COPY.loading}
      combineDecks={false}
      onClose={closeModal}
      onSelect={ids => {
        if (silhouetteSession) {
          onSelectSilhouette(ids[0] || "");
        } else {
          onSelectDetail(ids[0] || "");
        }
        closeModal();
      }}
    />
  );

  if (inline) {
    return (
      <>
        {trendControl}
        {sketchModal}
      </>
    );
  }

  return (
    <div className={sectionClassName}>
      {trendControl}
      {sketchModal}
    </div>
  );
};

export default ImageGeneratorForecastSilhouettePicker;
